import { Fragment, useMemo, useState } from 'react'

import { PageState } from '../app/PublicLayout.jsx'
import { assetUrl, thumbUrl, useCollection } from '../app/useSiteData.js'
import { groupByYear, parseAuthors } from '../lib/format.js'
import { useI18n } from '../lib/i18n.jsx'
import {
  Button,
  Chip,
  Container,
  EmptyState,
  ExternalLink,
  Media,
  PageHead,
  Row,
  RowItem,
  RowList,
  Section,
  cx,
  useToast,
} from '../ui/index.js'
import { collectMemberNames } from './Members.jsx'

/**
 * Publications.
 *
 * The densest page on the site, and the one a peer judges the group by. Three
 * decisions carry it.
 *
 * First, rows rather than cards. A card grid of twenty papers is two screens of
 * whitespace; a hairline row list is twenty scannable lines with the title,
 * authors, venue and links all on the same left edge.
 *
 * Second, a facet rail rather than a search box. Someone looking for "the CVPR
 * papers" does not know the title they want — they know a year, a venue or a
 * type, and each facet carries its own count so an empty result is visible
 * before it is chosen. Counts are computed against the *other* groups'
 * selections, which is what makes them stay honest as filters accumulate.
 *
 * Third, venue is never colour-coded. A list where CVPR is red and NeurIPS is
 * green stops being a bibliography and becomes a candy shop; the venue gets
 * weight and a hairline chip, and the single accent-tinted chip per row is
 * reserved for an award that was actually earned.
 *
 * 论文页。
 *
 * 全站信息密度最高的一页，也是同行据以判断这个组的一页。三个决定支撑着它。
 *
 * 其一，用行而不是卡片。二十篇论文做成卡片栅格就是两屏留白；发丝线行列表则是二十行
 * 可扫读的内容，标题、作者、发表载体和链接都对齐在同一条左边界上。
 *
 * 其二，用筛选栏而不是搜索框。想找「那几篇 CVPR」的人并不知道自己要的标题——他知道
 * 的是年份、载体或类型；每个筛选项自带计数，因此「选了会空」在点下去之前就看得见。
 * 计数是针对「其他分组」的当前选择算出来的，这才让它在筛选逐层叠加时依然诚实。
 *
 * 其三，发表载体绝不做颜色编码。CVPR 标红、NeurIPS 标绿的列表就不再是文献目录而是
 * 糖果店；载体只给字重和一个发丝线标签片，每行唯一带强调色的标签片留给真正拿到的
 * 奖项。
 */

/** Facet groups, in rail order. 筛选分组，按侧栏顺序。 */
const FACET_GROUPS = [
  { key: 'year', labelKey: 'publications.filterYear' },
  { key: 'venue', labelKey: 'publications.filterVenue' },
  { key: 'type', labelKey: 'publications.filterType' },
]

/** Options shown before the "+ N more" toggle appears. 出现「展开其余」之前显示的选项数。 */
const FACET_PREVIEW = 6

/** UI string key per publication type. 论文类型对应的文案 key。 */
const TYPE_LABEL_KEY = {
  journal: 'publications.typeJournal',
  conference: 'publications.typeConference',
  preprint: 'publications.typePreprint',
}

/** Empty selection, one array per facet group. 空选择状态，每个分组一个数组。 */
const NO_SELECTION = { year: [], venue: [], type: [] }

/**
 * The value a record contributes to one facet group.
 *
 * 一条记录在某个筛选分组上的取值。
 *
 * @param {object} record Publication record / 论文记录。
 * @param {string} groupKey Facet group key / 筛选分组键名。
 * @returns {string} Facet value, '' when the record has none / 取值；没有则为空串。
 */
function facetValue(record, groupKey) {
  if (groupKey === 'year') return String(record.year || '')
  if (groupKey === 'venue') return record.venueShort || record.venue || ''
  return record.type || ''
}

/**
 * Whether a record survives the current selection.
 *
 * `exceptGroup` lets a group's own options be counted against everything *but*
 * itself — the standard faceted-search rule, and the reason ticking a second
 * year does not drive every other year's count to zero.
 *
 * 判断一条记录是否通过当前筛选。exceptGroup 让某个分组的选项在计数时忽略它自己的
 * 选择——这是分面搜索的通行规则，也是「勾上第二个年份不会把其他年份计数全打成 0」
 * 的原因。
 *
 * @param {object} record Publication record / 论文记录。
 * @param {object} selected Selected values per group / 各分组已选值。
 * @param {string} [exceptGroup] Group to ignore / 要忽略的分组。
 * @returns {boolean} True when it matches / 通过则为 true。
 */
function matchesSelection(record, selected, exceptGroup) {
  return FACET_GROUPS.every((group) => {
    if (group.key === exceptGroup) return true
    const chosen = selected[group.key]
    return !chosen.length || chosen.includes(facetValue(record, group.key))
  })
}

/**
 * Resolve a DOI to a URL, accepting either a bare DOI or a full link.
 *
 * 把 DOI 解析成 URL，裸 DOI 和完整链接都接受。
 *
 * @param {string} doi DOI or URL / DOI 或链接。
 * @returns {string} Resolvable URL, or '' / 可访问的 URL，无则为空串。
 */
function doiUrl(doi) {
  if (!doi) return ''
  return /^https?:/i.test(doi) ? doi : `https://doi.org/${doi}`
}

/**
 * One publication row.
 *
 * Exported so the home page's "selected publications" band renders papers in
 * exactly this format rather than a prettier summary that then disagrees with
 * the real list.
 *
 * 一条论文行。导出给首页的「代表性论文」区块使用，让它以完全相同的格式渲染，
 * 而不是另做一个更好看、却和真正的列表对不上的摘要版本。
 *
 * @param {object} props Props / 属性。
 * @param {object} props.record Publication record / 论文记录。
 * @param {string[]} [props.memberNames] Names to bold in the author list / 作者串中需要加粗的姓名。
 * @returns {JSX.Element} Row / 列表行。
 */
export function PublicationRow({ record, memberNames = [] }) {
  const { t, pick } = useI18n()
  const toast = useToast()

  const authors = parseAuthors(record.authors, memberNames)
  const venue = record.venueShort || record.venue
  const highlight = pick(record.highlight)
  const thumbnail = thumbUrl(record.thumbnail)
  const pdf = assetUrl(record.pdf)
  const doi = doiUrl(record.doi)
  // Send the title wherever the paper actually lives, preferring the version
  // the authors chose to publish over a mirror we happen to host.
  // 标题链到论文真正所在的位置，优先作者选定的发布版本，而不是我们恰好托管的副本。
  const primary = record.url || doi || pdf

  /**
   * Copy the BibTeX entry to the clipboard.
   *
   * The clipboard API rejects outside a secure context, which is exactly the
   * case where silently doing nothing looks like a broken button.
   *
   * 把 BibTeX 复制到剪贴板。剪贴板 API 在非安全上下文下会 reject，而那正是
   * 「什么都不做」看起来像按钮坏了的情形。
   *
   * @returns {Promise<void>}
   */
  const copyBibtex = async () => {
    try {
      await navigator.clipboard.writeText(record.bibtex)
      toast.success(t('publications.copied'))
    } catch {
      toast.error(t('error.generic'))
    }
  }

  return (
    <RowItem
      className="pub-row"
      // A missing thumbnail collapses the grid to one column rather than
      // reserving an empty 72px gutter down the whole list.
      // 没有缩略图时栅格塌成单列，而不是在整张列表左侧留一条 72px 的空槽。
      meta={
        thumbnail ? (
          <Media className="pub-row__thumb">
            <img src={thumbnail} alt="" loading="lazy" decoding="async" />
          </Media>
        ) : undefined
      }
    >
      <h3 className="pub-row__title">
        {primary ? (
          <ExternalLink href={primary} glyph={false}>
            {record.title}
          </ExternalLink>
        ) : (
          record.title
        )}
      </h3>

      <p className="pub-row__authors">
        {authors.map((author, index) => (
          <Fragment key={`${author.name}-${index}`}>
            {index > 0 && <span className="pub-row__separator">, </span>}
            <span className={cx('pub-row__author', author.highlight && 'is-member')}>{author.name}</span>
          </Fragment>
        ))}
      </p>

      <p className="pub-row__meta">
        {venue && <span className="pub-row__venue">{venue}</span>}
        <span className="pub-row__year">{record.year}</span>
        {highlight && <Chip className="pub-row__award">{highlight}</Chip>}
        {record.citations > 0 && (
          <span className="pub-row__citations">{t('publications.citations', { n: record.citations })}</span>
        )}
      </p>

      <Row className="pub-row__links" gap="tight">
        {record.url && (
          <ExternalLink className="pub-row__link" href={record.url}>
            {t('publications.paper')}
          </ExternalLink>
        )}
        {pdf && (
          <ExternalLink className="pub-row__link" href={pdf}>
            {t('publications.pdf')}
          </ExternalLink>
        )}
        {record.codeUrl && (
          <ExternalLink className="pub-row__link" href={record.codeUrl}>
            {t('publications.code')}
          </ExternalLink>
        )}
        {doi && (
          <ExternalLink className="pub-row__link" href={doi}>
            {t('publications.doi')}
          </ExternalLink>
        )}
        {record.bibtex && (
          <button type="button" className="pub-row__link pub-row__link--action" onClick={copyBibtex}>
            {t('publications.copyBibtex')}
          </button>
        )}
      </Row>
    </RowItem>
  )
}

/**
 * Publications page.
 *
 * 论文页。
 *
 * @returns {JSX.Element} Page / 页面。
 */
export default function Publications() {
  const { t } = useI18n()
  const { data: records, loading, error, reload } = useCollection('publications')
  const { data: members } = useCollection('members')
  const [selected, setSelected] = useState(NO_SELECTION)
  const [expanded, setExpanded] = useState({})

  const memberNames = useMemo(() => collectMemberNames(members), [members])
  const all = useMemo(() => records || [], [records])

  const facets = useMemo(
    () =>
      FACET_GROUPS.map((group) => {
        const pool = all.filter((record) => matchesSelection(record, selected, group.key))
        const counts = new Map()
        for (const record of pool) {
          const value = facetValue(record, group.key)
          if (!value) continue
          counts.set(value, (counts.get(value) || 0) + 1)
        }
        const options = [...counts.entries()].map(([value, count]) => ({ value, count }))
        // Years read newest-first; the other groups read most-populated-first,
        // which puts the venues the group actually publishes in at the top.
        // 年份按由新到旧；其他分组按数量从多到少，这样课题组真正发表的载体排在最前。
        options.sort((a, b) =>
          group.key === 'year'
            ? b.value.localeCompare(a.value, 'en', { numeric: true })
            : b.count - a.count || a.value.localeCompare(b.value)
        )
        return { ...group, options }
      }),
    [all, selected]
  )

  const filtered = useMemo(
    () => all.filter((record) => matchesSelection(record, selected)),
    [all, selected]
  )
  const years = useMemo(() => groupByYear(filtered, (record) => record.year), [filtered])

  const activeFacets = FACET_GROUPS.flatMap((group) =>
    selected[group.key].map((value) => ({ group: group.key, value }))
  )

  /**
   * Add or remove one facet value.
   *
   * 增删一个筛选值。
   *
   * @param {string} groupKey Facet group key / 筛选分组键名。
   * @param {string} value Option value / 选项值。
   * @returns {void}
   */
  const toggleFacet = (groupKey, value) => {
    setSelected((current) => {
      const chosen = current[groupKey]
      return {
        ...current,
        [groupKey]: chosen.includes(value) ? chosen.filter((item) => item !== value) : [...chosen, value],
      }
    })
  }

  /**
   * Localise a facet option; only `type` has translated values.
   *
   * 本地化筛选项，只有 type 的取值需要翻译。
   *
   * @param {string} groupKey Facet group key / 筛选分组键名。
   * @param {string} value Option value / 选项值。
   * @returns {string} Display label / 显示文本。
   */
  const optionLabel = (groupKey, value) =>
    groupKey === 'type' && TYPE_LABEL_KEY[value] ? t(TYPE_LABEL_KEY[value]) : value

  return (
    <Section className="publications-page">
      <Container width="max">
        <PageHead title={t('publications.title')} lede={t('publications.lede')} />

        <PageState
          loading={loading}
          error={error}
          onRetry={reload}
          isEmpty={all.length === 0}
          empty={<EmptyState title={t('publications.empty')} body={t('publications.emptyBody')} />}
        >
          <div className="pub-layout">
            <aside className="pub-facets" aria-label={t('publications.filters')}>
              {facets.map((group) => {
                const isOpen = Boolean(expanded[group.key])
                const visible = isOpen ? group.options : group.options.slice(0, FACET_PREVIEW)
                const hidden = group.options.length - visible.length

                return (
                  <div className="pub-facets__group" key={group.key}>
                    <h2 className="pub-facets__heading">{t(group.labelKey)}</h2>
                    <ul className="pub-facets__options">
                      {visible.map((option) => (
                        <li className="pub-facets__option" key={option.value}>
                          <label className="pub-facets__label">
                            <input
                              className="pub-facets__checkbox"
                              type="checkbox"
                              checked={selected[group.key].includes(option.value)}
                              onChange={() => toggleFacet(group.key, option.value)}
                            />
                            <span className="pub-facets__text">{optionLabel(group.key, option.value)}</span>
                            <span className="pub-facets__count">{option.count}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                    {(hidden > 0 || isOpen) && (
                      <button
                        type="button"
                        className="pub-facets__more"
                        onClick={() => setExpanded((current) => ({ ...current, [group.key]: !isOpen }))}
                      >
                        {isOpen ? t('publications.showLess') : t('publications.showMore', { n: hidden })}
                      </button>
                    )}
                  </div>
                )
              })}
            </aside>

            <div className="pub-main">
              {/* The same facets as a chip row. The rail is hidden below the lg
                  breakpoint and this takes over — one source of truth, two
                  presentations, no second filtering implementation.
                  同一组筛选的标签片形式。侧栏在 lg 断点以下隐藏，由它接管——
                  一份数据、两种呈现，不存在第二套筛选实现。 */}
              {/* One row per group, not one flat list of every option. Flattened,
                  the three groups came to some forty chips that wrapped into nine
                  rows — a wall a reader had to scroll past before reaching the first
                  paper, on the page where the papers are the point. Kept grouped,
                  each row scrolls sideways on its own and the whole filter costs
                  three lines. The label earns its place here in a way it does not in
                  the desktop rail: once the rows scroll, "2019" and "CVPR" sitting on
                  separate lines is the only thing saying they filter different things.
                  每组一行，而不是把所有选项摊成一条扁平列表。摊平之后，三个分组共约四十个
                  标签片会折成九行——读者要先划过一堵墙才看得到第一篇论文，而这一页的主角
                  正是论文。保持分组，每行各自横向滚动，整个筛选区只占三行。
                  组名在这里的价值和它在桌面侧栏里不同：一旦各行可以滚动，
                  「2019」和「CVPR」分处两行就成了唯一能说明「它们筛的不是同一件事」的线索。 */}
              <div className="pub-facets-compact">
                {facets.map((group) => (
                  <div className="pub-facets-compact__group" key={group.key}>
                    <span className="pub-facets-compact__label">{t(group.labelKey)}</span>
                    <Row className="pub-facets-compact__track" gap="tight">
                      {group.options.map((option) => (
                        <Chip
                          key={`${group.key}:${option.value}`}
                          className="pub-facets-compact__chip"
                          active={selected[group.key].includes(option.value)}
                          onClick={() => toggleFacet(group.key, option.value)}
                        >
                          {optionLabel(group.key, option.value)}
                        </Chip>
                      ))}
                    </Row>
                  </div>
                ))}
              </div>

              <Row className="pub-toolbar" justify="between">
                <span className="pub-toolbar__count">
                  {t('publications.showing', { n: filtered.length, total: all.length })}
                </span>
                {activeFacets.length > 0 && (
                  <button type="button" className="pub-toolbar__clear" onClick={() => setSelected(NO_SELECTION)}>
                    {t('publications.clearFilters')}
                  </button>
                )}
              </Row>

              {activeFacets.length > 0 && (
                <Row className="pub-active" gap="tight">
                  {activeFacets.map((facet) => (
                    <Chip
                      key={`${facet.group}:${facet.value}`}
                      className="pub-active__chip"
                      active
                      title={t('common.remove')}
                      onClick={() => toggleFacet(facet.group, facet.value)}
                    >
                      {optionLabel(facet.group, facet.value)}
                      <span className="pub-active__remove" aria-hidden="true">
                        ×
                      </span>
                    </Chip>
                  ))}
                </Row>
              )}

              {filtered.length === 0 ? (
                <EmptyState
                  className="pub-nomatch"
                  title={t('publications.noMatch')}
                  action={
                    <Button variant="ghost" onClick={() => setSelected(NO_SELECTION)}>
                      {t('publications.clearFilters')}
                    </Button>
                  }
                />
              ) : (
                years.map((bucket) => (
                  <section className="pub-year" key={bucket.year}>
                    <h2 className="pub-year__heading">{bucket.year}</h2>
                    <RowList className="pub-list">
                      {bucket.items.map((record) => (
                        <PublicationRow key={record.id} record={record} memberNames={memberNames} />
                      ))}
                    </RowList>
                  </section>
                ))
              )}
            </div>
          </div>
        </PageState>
      </Container>
    </Section>
  )
}
